<script>
  import { surveyStore } from '../../../stores/surveyStore';
  
  // Direct image imports for seating options
  import loudvilleImg from '../../../assets/images/seating-loudville.png';
  import lowerImg from '../../../assets/images/seating-lower.png';
  import clubImg from '../../../assets/images/seating-mezzanine.png';
  import floorImg from '../../../assets/images/seating-floor.png';
  import premiumImg from '../../../assets/images/seating-club.png';
  
  export let options = [];
  export let questionId = '';
  export let title = '';
  
  // Get the current selection from the store
  $: selected = $surveyStore[questionId];
  
  // Track image loading state
  let imageLoadStatus = {};
  
  // Handle option selection
  function selectOption(optionId) {
    surveyStore.update(store => ({
      ...store,
      [questionId]: optionId
    }));
  }
  
  // Helper function to get the image URL
  function getImageUrl(option) {
    // If the option already has a complete image URL, use it
    if (option.image && option.image.startsWith('http')) {
      return option.image;
    }
    
    // For seating options, use our directly imported images
    if (questionId.includes('seating') || 
        (option.id && (
          option.id.includes('a') || 
          option.id.includes('c') || 
          option.id.includes('b') || 
          option.id.includes('d')
        ))) {
      
      // Return a specific image based on the seating type
      if (option.id.includes('a')) {
        return loudvilleImg;
      } else if (option.id.includes('c')) {
        return lowerImg;
      } else if (option.id.includes('b')) {
        return clubImg;
      } else if (option.id.includes('d')) {
        return premiumImg;
      }
    }

    
    // If option has its own image, use that
    if (option.image) {
      return option.image;
    }
    
    // Fallback to placeholder
    return 'https://via.placeholder.com/300x200?text=' + encodeURIComponent(option.label || 'Option Image');
  }

  // Handle image load success
  function handleImageLoad(optionId) {
    imageLoadStatus = { ...imageLoadStatus, [optionId]: 'loaded' };
  }

  // Handle image load error
  function handleImageError(optionId, option) {
    console.error(`Failed to load image for option: ${optionId}`);
    imageLoadStatus = { ...imageLoadStatus, [optionId]: 'error' };
  }
</script>

<div class="image-multi-choice">
  {#each options as option (option.id)}
    <div 
      class="option-card {selected === option.id ? 'selected' : ''}"
      on:click={() => selectOption(option.id)}
      on:keypress={(e) => e.key === 'Enter' && selectOption(option.id)}
      tabindex="0"
      role="button"
      aria-pressed={selected === option.id}
      data-option-id={option.id}
    >
      <!-- Image container with proper aspect ratio -->
      <div class="image-container">
        {#if !imageLoadStatus[option.id] || imageLoadStatus[option.id] !== 'error'}
          <img 
            src={getImageUrl(option)} 
            alt={option.label} 
            on:load={() => handleImageLoad(option.id)}
            on:error={() => handleImageError(option.id, option)}
          />
          
          {#if !imageLoadStatus[option.id] || imageLoadStatus[option.id] === 'loading'}
            <div class="image-loader">
              <div class="spinner"></div>
            </div>
          {/if}
        {:else}
          <div class="image-placeholder">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3 3.5-4.5 4.5 6H5l3.5-4.5z" fill="#999"/>
            </svg>
          </div>
        {/if}
      </div>
      
      <!-- All-Inclusive tag for the Premium seating tile -->
      {#if questionId.includes('seating') && option.id === 'd'}
        <div class="tile-tag">All-Inclusive</div>
      {/if}

      <!-- Caption area -->
      <div class="option-caption">
        <h3>{option.label}</h3>
      </div>
      
      <!-- Selection indicator -->
      {#if selected === option.id}
        <div class="selection-badge">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white"/>
          </svg>
        </div>
      {/if}
    </div>
  {/each}
</div>

<style>
  .image-multi-choice {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 24px;
    width: 100%;
    margin: 0 auto 24px;
  }
  
  .option-card {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    background-color: white;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    height: 100%;
    border: 2px solid transparent;
    text-align: center; 
  }
  
  .option-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  
  .option-card.selected {
    border-color: #6A0032; /* Cavs wine color */
    box-shadow: 0 0 0 2px rgba(134, 0, 56, 0.2), 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  
  .image-container {
    height: 180px;
    width: 100%;
    position: relative;
    overflow: hidden;
  }
  
  .image-container img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  .option-card:hover .image-container img {
    transform: scale(1.05);
  }
  
  .option-caption {
    padding: 16px;
    text-align: center;
    background-color: white;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center; /* Add this to center content horizontally */
    width: 100%; /* Ensure full width */
  }
  
  .option-caption h3 {
    margin: 0 auto; /* Center the text block */
    font-size: 16px;
    font-weight: 600;
    color: #333;
    text-align: center; /* Force center alignment */
    max-width: 100%; /* Ensure text wraps properly */
    word-wrap: break-word; /* Handle long words */
  }
  
  .tile-tag {
    position: absolute;
    top: 12px;
    left: 12px;
    background-color: #BB945C; /* Cavs gold */
    color: #111;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 999px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    z-index: 2;
  }

  .selection-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    background-color: #6A0032; /* Cavs wine color */
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    animation: pop-in 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  
  @keyframes pop-in {
    0% { transform: scale(0); opacity: 0; }
    70% { transform: scale(1.2); }
    100% { transform: scale(1); opacity: 1; }
  }
  
  .image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f0f0f0;
  }
  
  .image-loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.7);
  }
  
  .spinner {
    width: 30px;
    height: 30px;
    border: 3px solid rgba(134, 0, 56, 0.1);
    border-radius: 50%;
    border-top-color: #6A0032; /* Cavs wine color */
    animation: spin 1s ease-in-out infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  /* Responsive adjustments */
  @media (max-width: 767px) {
    .image-multi-choice {
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 16px;
    }
    
    .image-container {
      height: 140px;
    }
    
    .option-caption {
      padding: 12px;
    }
    
    .option-caption h3 {
      font-size: 14px;
    }

    .option-card:last-child .option-caption h3 {
    font-size: 15px; /* Slightly smaller font for longer captions */
    line-height: 1.3; /* Better line spacing for readability */
  }
  }
</style>